interface Activity {
  id: string;
  type: string;
  description: string;
  actorName: string;
  createdAt: string | Date;
  step?: {label: string} | null;
}

interface OnboardingTimelineProps {
  activities: Activity[];
  loading?: boolean;
}

const dotColors: Record<string, string> = {
  STEP_COMPLETED: 'bg-emerald-500',
  STEP_BLOCKED: 'bg-red-500',
  NOTE_ADDED: 'bg-blue-500',
};

function getRelativeTime(date: string | Date): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

export function OnboardingTimeline({activities, loading}: OnboardingTimelineProps) {
  if (loading) {
    return <p className='text-sm text-gray-500'>Loading...</p>;
  }

  if (activities.length === 0) {
    return <p className='text-sm text-gray-500'>No activity yet.</p>;
  }

  return (
    <div className='border-l-2 border-gray-200 ml-1'>
      {activities.map((activity) => (
        <div key={activity.id} className='relative pl-6 pb-4'>
          <span
            className={`absolute left-[-5px] top-1 w-2 h-2 rounded-full ${dotColors[activity.type] ?? 'bg-gray-400'}`}
          />
          <p className='text-sm text-gray-900'>{activity.description}</p>
          <div className='flex items-center gap-2 mt-0.5'>
            <span className='text-xs text-gray-500'>
              {activity.actorName} &middot; {getRelativeTime(activity.createdAt)}
            </span>
            {activity.step && (
              <span className='inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-gray-100 text-gray-600'>
                {activity.step.label}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
